import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { MeAIProvider } from "@/context/MeAIContext";
import { WorkStreamProvider } from "@/context/WorkStreamContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { PreviewBadge } from "@/components/environment/PreviewBadge";

import LoginPage from "@/pages/LoginPage";
import MemberHome from "@/pages/MemberHome";
import MedicalPage from "@/pages/MedicalPage";
import LifestylePage from "@/pages/LifestylePage";
import LifePage from "@/pages/LifePage";
import AssistantPage from "@/pages/AssistantPage";
import DevicesPage from "@/pages/DevicesPage";
import PluginsPage from "@/pages/PluginsPage";
import ConsciousPage from "@/pages/ConsciousPage";
import PersonPage from "@/pages/PersonPage";
import CognitivePage from "@/pages/CognitivePage";
import IntentPage from "@/pages/IntentPage";
import ProductionPage from "@/pages/ProductionPage";
import KnowledgePage from "@/pages/KnowledgePage";
import CognitiveStatePage from "@/pages/CognitiveStatePage";
import MeetPage from "@/pages/MeetPage";
import WatchRoomPage from "@/pages/WatchRoomPage";
import PilotDashboardPage from "@/pages/PilotDashboardPage";
import LivingPresencePage from "@/pages/LivingPresencePage";
import { ClinicianHome, ClinicianPatientDetail } from "@/pages/ClinicianPages";
import { AdminHome, AdminBrainStatus } from "@/pages/AdminPages";

function RootDispatcher() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  // The first living experience is the same for every role: the orb.
  // Power users open the workspace via the small top-right pill.
  return <LivingPresencePage />;
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "clinician") return <Navigate to="/clinician" replace />;
  if (user.role === "administrator") return <Navigate to="/admin" replace />;
  return <MemberHome />;
}

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <MeAIProvider>
          <WorkStreamProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/meet" element={<MeetPage />} />
            <Route path="/watch/:code" element={<WatchRoomPage />} />
            <Route
              path="/pilot"
              element={
                <ProtectedRoute>
                  <PilotDashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RootDispatcher />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Shell>
                    <DashboardRedirect />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/medical"
              element={
                <ProtectedRoute roles={["member"]}>
                  <Shell>
                    <MedicalPage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lifestyle"
              element={
                <ProtectedRoute roles={["member"]}>
                  <Shell>
                    <LifestylePage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assistant"
              element={
                <ProtectedRoute>
                  <Shell>
                    <AssistantPage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/devices"
              element={
                <ProtectedRoute>
                  <Shell>
                    <DevicesPage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/life"
              element={
                <ProtectedRoute roles={["member"]}>
                  <Shell>
                    <LifePage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/plugins"
              element={
                <ProtectedRoute>
                  <Shell>
                    <PluginsPage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/conscious"
              element={
                <ProtectedRoute>
                  <Shell>
                    <ConsciousPage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/persons"
              element={
                <ProtectedRoute>
                  <Shell>
                    <PersonPage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cognitive"
              element={
                <ProtectedRoute>
                  <Shell>
                    <CognitivePage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intent"
              element={
                <ProtectedRoute>
                  <Shell>
                    <IntentPage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production"
              element={
                <ProtectedRoute>
                  <Shell>
                    <ProductionPage />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/knowledge"
              element={
                <ProtectedRoute>
                  <Shell>
                    <KnowledgePage />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/clinician"
              element={
                <ProtectedRoute roles={["clinician", "administrator"]}>
                  <Shell>
                    <ClinicianHome />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinician/:id"
              element={
                <ProtectedRoute roles={["clinician", "administrator"]}>
                  <Shell>
                    <ClinicianPatientDetail />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["administrator"]}>
                  <Shell>
                    <AdminHome />
                  </Shell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/brain"
              element={
                <ProtectedRoute roles={["administrator"]}>
                  <Shell>
                    <AdminBrainStatus />
                  </Shell>
                </ProtectedRoute>
              }
            />

            <Route
              path="/cognitive-state"
              element={
                <ProtectedRoute>
                  <CognitiveStatePage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <PreviewBadge />
          </WorkStreamProvider>
        </MeAIProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}
